<script setup>
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import 'devextreme-vue/text-area'
import { DxDataGrid, DxGroupItem, DxSummary } from 'devextreme-vue/data-grid'

import { isCrossDay, minutesToHHmm } from '@/lib/utils'
import { menuRoutesEnum } from '@/router'

import * as dataGrid from '@/composables/data-grid'
import { useWorkTimeOverlap, endEditorPreparing, createStopButton } from '@/composables/garage/work-time-events'
import { useColumns } from '@/composables/garage/work-time-event-grid'

import InformationToolTip from '@/components/shared/InformationToolTip.vue'
import ReferenceLink from '@/components/shared/ReferenceLink.vue'

import { useDocumentService } from '@/services/document.service'
import { useGarageStore } from '@/stores/garage.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'

import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import WorkTimeOverlapWarningPopup from '@/components/shared/WorkTimeOverlapWarningPopup.vue'

import { garageApi } from '@/apis/garage.api'
import { useEntityLock } from '@/services/entity-lock.service'

const garageStore = useGarageStore()
const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const workTimeOverlap = useWorkTimeOverlap()

const { gridRef, refreshGridAsync, focusGrid } = dataGrid.useGridRef()
const { extractReferenceData } = useDocumentService()

const saveStateId = `work-time-event-grid`
const gridId = `${saveStateId}`

const i18n = getI18n()

const editors = {}
const computedDataSource = computed(() => {
  return dataGrid.useODataSource(
    garageApi.getAbsoluteUrl() + garageApi.workTimeEntriesUrl,
    {
      customParamsFn: () => { return { $notop: true } },
      avoidDuplicateDatasourceIds: true,
    }
  )
})

const entityLock = useEntityLock({
  baseLockUrl: garageStore.api.getAbsoluteUrl() + garageStore.api.workTimeEntriesUrl,
  modelName: 'WorkTimeEntry',
  gridRef,
})

const selectedRows = ref([])
const formInstance = ref(null)

const columns = useColumns(editors)

const onStopClick = (e) => {
  garageApi.updateWorkTimeEntryAsync(e.id, { end: new Date().toISOString() }).then(() => {
    refreshGridAsync()
  })
}

const rowButtons = entityLock.getEntityLockButtons(
  {
    visible: {
      edit: (e) => (!e.data.worksheet.has_sale_document || registryStore.companyData.allow_work_time_entries_on_closed_worksheet) && !entityLock.isLocked(false, e.entityId),
      delete: (e) => (!e.data.worksheet.has_sale_document || registryStore.companyData.allow_work_time_entries_on_closed_worksheet) && !entityLock.isLocked(false, e.entityId),
      view: (e) => (e.data.worksheet.has_sale_document && !registryStore.companyData.allow_work_time_entries_on_closed_worksheet) || entityLock.isLocked(false, e.entityId),
    }
  }
)
rowButtons.push(
  createStopButton({
    onStopClick,
    isVisible: (e) =>
      e.data.end === null &&
      !isCrossDay(e.data.start)
  })
)

const { defaultActionTitle } = dataGrid.useDefaultDialogTitle()

const dialogTitle = (i) => {
  let title = `${defaultActionTitle(i)} ${i18n.t('common.workTimeEntry')}`
  if (i.id && i.mechanic) {
    const mechanic = webSiteStore.userDetails.find(u => u.id === i.mechanic)
    title += ` - ${mechanic?.name + ' ' + mechanic?.name_ext || ''}`
  }
  return `${title} | ${i.worksheet.number_mnemonic}/${i.worksheet.series}`
}

const onInitialized = (e) => {
  formInstance.value = e.component
}

const editing = dataGrid.useDefaultEditing(dialogTitle, {
  colCount: 1,
  items: [
    {
      itemType: 'group',
      caption: i18n.t('common.mainInfo'),
      colCount: 3,
      items: [
        {
          dataField: 'start',
          editorOptions: {
            type: 'datetime',
            displayFormat: {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            },
            max: new Date(Date.now())
          }
        },
        {
          dataField: 'end',
          editorOptions: {
            type: 'datetime',
            displayFormat: {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            },
          }
        },
        {
          dataField: 'duration_minutes', label: { text: `${i18n.t('common.duration')} (${i18n.t('common.minuteAlt')})` }
        },
        { dataField: 'mechanic' },
        {
          dataField: 'notes', colSpan: 2
        },
        { dataField: 'is_manual', editorType: 'dxCheckBox', editorOptions: { disabled: true }, label: { text: i18n.t('common.isManuallyInserted'), location: 'left' } },
      ]
    }
  ],
  onInitialized: onInitialized,
}, {
  allowAdding: false,
  allowUpdating: registryStore.companyData.allow_work_time_entries_on_closed_worksheet,
  allowDeleting: registryStore.companyData.allow_work_time_entries_on_closed_worksheet,
  onInitializedCustom: (e) => onInitializedCustom(e),
  entityLock: entityLock,
  width: '50rem'
})

const isReadOnly = ref(false)
const onInitializedCustom = (e) => {
  if (isReadOnly.value && !registryStore.companyData.allow_work_time_entries_on_closed_worksheet) {
    const toolbarItems = e.component.option('toolbarItems') || []

    const updatedToolbarItems = toolbarItems.map((item, index) => {
      if (index === toolbarItems.length - 1) return item
      return {
        ...item,
        visible: !isReadOnly.value,
      }
    })
    e.component.option('toolbarItems', updatedToolbarItems)
  } else {
    entityLock.readOnlyToolbarItems(e)
  }
}

const onRowInserting = async(e) => {
  e.data.start = new Date(e.data.start).toISOString()
  e.data.activity_code = garageStore.workActivityEnum.WORKSHEET_PROCESSING
}

const onSaved = async(e) => { await refreshGridAsync() }

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData
}

const batchDeleteAsync = async(ids) => {
  await garageApi.batchDeleteWorkTimeEntriesAsync(ids)
}

const onToolbarPreparing = (e) => {
  dataGrid.useDefaultToolbar(e, { allowBatchDelete: true })
}

const onEditorPreparing = (e) => {
  endEditorPreparing(e, editors)

  if (e.parentType === 'dataRow' && !registryStore.companyData.allow_work_time_entries_on_closed_worksheet) {
    e.editorOptions.readOnly = isReadOnly.value
  }
}

const onPopupCancel = (e) => {
  workTimeOverlap.showModal.value = false
}

const onPopupSave = (e) => {
  workTimeOverlap.showModal.value = false
}

const { exportOptions, onExporting } = dataGrid.useDefaultExport({
  fileName: i18n.t('common.workTimeEntry', 2),
  isLandscape: true,
  formats: ['XLSX'],
  customizeCell: (options) => {
    const { gridCell, excelCell } = options

    if (gridCell?.column?.dataField === 'worksheet.id') {
      const worksheet = gridCell.data?.worksheet

      if (worksheet) {
        excelCell.value = [
          worksheet.prefix,
          worksheet.series,
          worksheet.number_mnemonic
        ]
        .filter(Boolean)
        .join(' ')
      }
    }
    if (gridCell?.column?.dataField === 'start') {
      if (gridCell.data?.start) {
        const date = new Date(gridCell.data.start)

        excelCell.value = date
        excelCell.numFmt = 'HH:mm'
      }
    }
    if (gridCell?.column?.dataField === 'end') {
      if (gridCell.data?.end) {
        const date = new Date(gridCell.data.end)

        excelCell.value = date
        excelCell.numFmt = 'HH:mm'
      }
    }
  }
})

const onFocusedRowChanged = (e) => {
  const rowData = e.row?.data
  if (e.row?.key == null || !rowData?.worksheet) return

  isReadOnly.value = rowData.worksheet.has_sale_document || entityLock.isLocked(false, rowData.id)
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  saveStateId: saveStateId,
  columns: columns,
  editing: editing,
  selection: dataGrid.useDefaultGridSelection(),
  onFocusedRowChanged,
  export: exportOptions,
  groupPanel: {
    visible: true,
    emptyPanelText: i18n.t('common.groupPanelPlaceholder', 2)
  },
  grouping: {
    contextMenuEnabled: true
  },
  onExporting: onExporting,
  onEditorPreparing: onEditorPreparing,
  onToolbarPreparing: onToolbarPreparing,
  onRowInserting: onRowInserting,
  onSelectionChanged: onSelectionChanged,
  onSaved: onSaved,
  focusGrid: focusGrid,
  onDataErrorOccurred: (e) => {
    if (workTimeOverlap.buildErrorMessage(e.error?.errorDetails)) {
      workTimeOverlap.showModal.value = true
      return
    }

    dataGrid.useDefaultErrorHandler(e)
  },
  onInitialized: (e) => dataGrid.useDefaultOnInitialized(e, {enableGrouping: true}),
  height: '90vh',
  remoteOperations: 'false',
  disabledFocusOnContentReady: true,
  splittedGridDetail: true,
  rowDragging: {
    allowReordering: false,
  },
})

const formatMinutesSummary = ({ value }) => {
  return `${i18n.t('common.total')}: ${minutesToHHmm(value)}`
}

</script>
<template>
  <div class="row-grid-ctn">
    <dx-data-grid
      ref="gridRef"
      :key="gridId"
      v-bind="gridOptions"
      :data-source="computedDataSource"
    >
      <dx-summary>
        <dx-group-item
          column="duration_minutes"
          summary-type="sum"
          :align-by-column="true"
          :customize-text="formatMinutesSummary"
        />
        <dx-group-item
          column="billed_minutes"
          summary-type="sum"
          :align-by-column="true"
          :customize-text="formatMinutesSummary"
        />
      </dx-summary>
      <template #customToolbarDeleteButtonTemplate>
        <custom-toolbar-delete-button-template
          :selected-rows-id="selectedRows?.map(i => i.id) || []"
          :grid="gridRef"
          :entity-lock="entityLock"
          :on-delete="batchDeleteAsync"
          :disabled="selectedRows?.some(row => row.worksheet.has_sale_document) || selectedRows.length === 0"
        />
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'WorkTimeEntry'"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="entityLock"
          :is-locked-available="true"
          :buttons="rowButtons"
        />
      </template>
      <template #worksheetRef="{ data: cellInfo }">
        <reference-link
          :data="{
            series: cellInfo.data.worksheet.series,
            number: cellInfo.data.worksheet.number_mnemonic
          }"
          :text-fields="['prefix', 'series', 'number']"
          :query-fields="['series', 'number']"
          :target="menuRoutesEnum.WORKSHEETS"
        >
          <information-tool-tip :data="extractReferenceData({ ...cellInfo.data.worksheet, type: portalStore.documentTypesEnum.GARAGE_WORKSHEET })" />
        </reference-link>
      </template>
    </dx-data-grid>
    <work-time-overlap-warning-popup
      v-if="workTimeOverlap.showModal.value"
      v-model:visible="workTimeOverlap.showModal.value"
      :options="{
        newWorkTimeEntry: workTimeOverlap.newWorkTimeEntry.value,
        mechanic: workTimeOverlap.warningOverlappedMechanic.value,
        workTimeEntries: workTimeOverlap.warningOverlappedWorkTimeEntries.value,
        worksheets: workTimeOverlap.warningOverlappedWorksheets.value,
        title: $t('error.validation.work_time_entry_overlap') }"
      @cancel="onPopupCancel"
      @save="onPopupSave"
    />
  </div>
</template>

<style scoped lang="scss">
:deep(.icon-red) {
  color: $negative !important;
}
</style>
